import { Link } from '@tanstack/react-router';
import { CalendarDays, CircleCheck, Clock, FileText, Mail, Users, type LucideIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { AdminAttentionItem, AdminAttentionKind, UnacceptedInvite } from '@nest-recruit/shared';
import { Button, buttonClassName } from '../../../../components/Button';
import { formatDate } from '../../../../lib/format';
import { useToasts } from '../../../../stores/toasts';
import { useSendInviteAgain } from '../../api/overview-api';
import { attentionDetail, attentionTitle } from './overview-words';
import styles from './AttentionList.module.css';

// ADMIN-21 "Needs your attention" (Option A of `1-shell/admin-home.html`): one line per thing that needs the Admin,
// in the server's order (most urgent first), each with the button that fixes it. Nothing is drawn for a kind with
// nothing in it, and an empty list says so calmly instead of drawing an empty card.

type Tone = 'bad' | 'wait' | 'info';

interface Fix {
  readonly icon: LucideIcon;
  readonly tone: Tone;
  readonly label: string;
  readonly to: string;
}

/** The invites line fixes itself in place ("Send again"), so it has no link here. */
const FIXES: Readonly<Record<Exclude<AdminAttentionKind, 'invites_unaccepted'>, Fix>> = {
  failed_emails_today: { icon: Mail, tone: 'bad', label: 'Open Email delivery', to: '/admin/email' },
  holidays_missing: { icon: CalendarDays, tone: 'wait', label: 'Add holidays', to: '/admin/calendars' },
  anonymising_soon: { icon: Clock, tone: 'info', label: 'Review list', to: '/admin/retention' },
  starter_templates: { icon: FileText, tone: 'info', label: 'Edit templates', to: '/admin/message-templates' },
};

/** On a phone the first three show, then "+ N more" (the drawn phone view). */
const PHONE_LINES = 3;

export function AttentionList({ items }: { readonly items: readonly AdminAttentionItem[] }): ReactNode {
  const [isExpanded, setIsExpanded] = useState(false);

  if (items.length === 0) {
    return (
      <div className={styles.calm}>
        <CircleCheck size={22} aria-hidden className={styles.calmIcon} />
        <div>
          <p className={styles.calmTitle}>Nothing needs you</p>
          <p className={styles.calmBody}>No failed emails today, no invitations waiting, and the holidays, anonymising and templates are in order.</p>
        </div>
      </div>
    );
  }

  const hidden = items.length - PHONE_LINES;
  return (
    <>
      <ul className={`${styles.list} ${isExpanded ? '' : styles.collapsed}`}>
        {items.map((item) => (
          <li key={item.kind} className={styles.line}>
            {item.kind === 'invites_unaccepted' ? <InvitesLine item={item} /> : <FixLine item={item} fix={FIXES[item.kind]} />}
          </li>
        ))}
      </ul>
      {hidden > 0 && !isExpanded && (
        <div className={styles.more}>
          <Button variant="quiet" size="sm" block onClick={() => setIsExpanded(true)}>
            + {hidden} more
          </Button>
        </div>
      )}
    </>
  );
}

function LineIcon({ icon: Icon, tone }: { readonly icon: LucideIcon; readonly tone: Tone }): ReactNode {
  return (
    <span className={styles.icon} data-tone={tone} aria-hidden>
      <Icon size={18} />
    </span>
  );
}

function FixLine({ item, fix }: { readonly item: AdminAttentionItem; readonly fix: Fix }): ReactNode {
  const detail = attentionDetail(item);
  return (
    <>
      <LineIcon icon={fix.icon} tone={fix.tone} />
      <div className={styles.text}>
        <p className={styles.title}>{attentionTitle(item)}</p>
        {detail && <p className={styles.detail}>{detail}</p>}
      </div>
      {/* Admin redesign batch 2: "Edit templates" opens the first template still on the starter wording. */}
      <Link to={fix.to} search={item.kind === 'starter_templates' && item.templates[0] ? { template: item.templates[0].id } : undefined} className={`${buttonClassName('quiet', 'sm')} ${styles.fix}`}>
        {fix.label}
      </Link>
    </>
  );
}

function InvitesLine({ item }: { readonly item: Extract<AdminAttentionItem, { kind: 'invites_unaccepted' }> }): ReactNode {
  const sendAgain = useSendInviteAgain();
  const showToast = useToasts((state) => state.show);

  // Act first (non-negotiable #11). Review R23: the invite is no longer waiting once it is sent again, so its row
  // leaves at once and both counts drop (`useSendInviteAgain`); a refusal puts it back and says why. There is no
  // Undo: the email has already gone (owner, 2026-11-02).
  const send = (invite: UnacceptedInvite): void => {
    // `mutateAsync`, not `mutate`'s callbacks: those fire only for the latest call, and two people may be sent
    // again one after the other before the first answers.
    sendAgain.mutateAsync(invite.inviteId).then(
      () => showToast({ tone: 'success', message: `Invitation sent again to ${invite.fullName}` }),
      (error: unknown) => {
        const detail = error instanceof Error ? error.message : undefined;
        showToast({ tone: 'danger', message: `Invitation to ${invite.fullName} not sent again`, ...(detail ? { detail } : {}) });
      },
    );
  };

  const more = item.count - item.invites.length;
  return (
    <>
      <LineIcon icon={Users} tone="wait" />
      <div className={styles.text}>
        <p className={styles.title}>{attentionTitle(item)}</p>
        <ul className={styles.invites}>
          {item.invites.map((invite) => (
            <li key={invite.inviteId} className={styles.invite}>
              <span className={styles.inviteText}>
                <span className={styles.inviteName}>{invite.fullName}</span>
                {` · ${invite.roleLabel} · sent ${formatDate(invite.sentAt)}`}
                {invite.isExpired ? ' · the link has run out' : ''}
              </span>
              <Button size="sm" onClick={() => send(invite)} aria-label={`Send the invitation to ${invite.fullName} again`}>
                Send again
              </Button>
            </li>
          ))}
        </ul>
        {more > 0 && (
          <Link to="/admin/users" className={styles.link}>
            {more === 1 ? '1 more in People' : `${more} more in People`}
          </Link>
        )}
      </div>
    </>
  );
}
